import { useStore } from 'vuex';

import {
  PRODUCT_IMAGES_CTA_ADD_ITEM,
  PRODUCT_IMAGES_CTA_UPDATE_ITEM,
  PRODUCT_IMAGES_CTA_REMOVE_ITEM,
} from '../../types/actions';

/**
 * @typedef {Object} ItemData
 * @property {string=} customCssClass - a custom css class to be applied to the CTA item
 * @property {string=} customStyle - custom styles to be applied to the CTA item
 * @property {string=} id - a unique id (string) to identify the CTA item
 * @property {boolean=} isAR - a flag that determines if the product is AR eligible
 * @property {string=} label - the text to display in the CTA
 * @property {function=} onClickHandler - a function which will be called on CTA click
 * @property {boolean=} showArrow - a boolean to determine if chevron (down arrow) must be shown along text
 * @property {string | null} [url] - the url to be set as href when CTA is of <a> type
 * @property {boolean} [allowOpeningInNewTab] - a boolean to determine if url should be opened in new tab
 */

/**
 * @typedef {Object} UpdateItemParams
 * @property {string} itemId - a unique id (string) that identifies the CTA item
 * @property {ItemData} itemData - the config data for the CTA item
 */

export function useProductImagesCtaActions() {
  const store = useStore();

  /**
   * Check if itemData is defined
   * @param {ItemData} itemData
   * @returns {boolean}
   */
  const isItemDataEmpty = (itemData) => !itemData || (typeof itemData === 'object' && Object.keys(itemData).length < 1);

  /**
   * Check item data validity
   * @param {ItemData} itemData
   * @param {boolean} checkRequired -
   * @returns {boolean}
   */
  const isItemDataValid = (itemData, checkRequired = true) => {
    // Check for required properties
    if (
      checkRequired
      && (
        !itemData.id
        || !itemData.label
        || !itemData.onClickHandler
      )) return false;

    // Type check
    if (
      (itemData.customCssClass && typeof itemData.customCssClass !== 'string')
      || (itemData.id && typeof itemData.id !== 'string')
      || (itemData.label && typeof itemData.label !== 'string')
      || (itemData.onClickHandler && typeof itemData.onClickHandler !== 'function')
      || (itemData.showArrow && typeof itemData.showArrow !== 'boolean')
    ) return false;

    return true;
  };

  /**
   * Call store add item method
   * @param {ItemData} itemData
   */
  const addCtaItem = (itemData) => {
    const itemsList = store?.state?.productImagesCtaStore?.itemsList || [];
    const isValid = !isItemDataEmpty(itemData) && isItemDataValid(itemData);

    if (isValid) {
      const itemId = itemData.id;
      const isAlreadyAdded = !!itemsList.find((item) => item.id === itemId);
      // Only add if the item was NOT previously added
      if (!isAlreadyAdded) {
        store.dispatch(`productImagesCtaStore/${PRODUCT_IMAGES_CTA_ADD_ITEM}`, itemData);
      }
    } else {
      /* eslint-disable no-console */
      console.warn('[product-images-cta][add-cta-item] Item data is not valid');
    }
  };

  /**
   * Call store remove item method
   * @param {string} itemId
   */
  const removeCtaItem = (itemId) => {
    const isValid = typeof itemId === 'string';
    if (isValid) {
      store.dispatch(`productImagesCtaStore/${PRODUCT_IMAGES_CTA_REMOVE_ITEM}`, itemId);
    } else {
      /* eslint-disable no-console */
      console.warn('[product-images-cta][remove-cta-item] Item data is not valid');
    }
  };

  /**
   * Call store update item method
   * You can pass only the item data properties that need update. The
   * passed properties will override any previously set property values.
   * @param {string} itemId
   * @param {ItemData} itemData
   */
  const updateCtaItem = (itemId, itemData) => {
    const isItemIdString = typeof itemId === 'string';

    const isValid = isItemIdString
      && !isItemDataEmpty(itemData)
      && isItemDataValid(itemData, false);

    if (isValid) {
      store.dispatch(`productImagesCtaStore/${PRODUCT_IMAGES_CTA_UPDATE_ITEM}`, {
        itemData,
        itemId,
      });
    } else {
      /* eslint-disable no-console */
      console.warn('[product-images-cta][update-cta-item] Item data is not valid');
    }
  };

  return {
    addCtaItem,
    removeCtaItem,
    updateCtaItem,
  };
}

export default useProductImagesCtaActions;
